import { useLayoutEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight, Heart } from 'lucide-react'
import { pointerGlow } from '../../animations/gsapAnimations'
import type { Vehicle } from '../../data/vehicles'
import { nameOf } from '../../data/vehicles'
import { vehicleWhatsappHref } from '../../data/site'
import { useColourPick } from '../../hooks/useColourPick'
import { pkr, shortPrice } from '../../utils/format'
import { purchaseOptionsFor, type PurchaseRoute } from '../../utils/purchase'
import ColourSwatches from '../vehicle/ColourSwatches'
import PurchaseRoutePicker from '../vehicle/PurchaseRoutePicker'
import BookingModal from '../booking/BookingModal'
import WhatsAppIcon from '../ui/WhatsAppIcon'
import VehicleMedia from '../vehicle/VehicleMedia'

/* The listing card: what it costs, a photograph, what the car is, and the
   paints it can be had in.

   It is deliberately quieter than the editorial card in the home page's rails.
   A grid of these is read by comparison — the eye runs down one column of
   names and one column of figures — so the assurance pills are left to the
   car's own page, one click away. The swatches stay: which colours are
   actually standing on the floor is a question people ask before they ask
   anything else, and answering it a click away means browsing a grid of
   Citys without ever being told there is a red one. They cost one short row
   and only appear on a car that has a sister in another paint.

   Pressing one says which colour this car is wanted in — see useColourPick.
   Nothing quoted on the card moves with it: the price, the monthly and the
   first payment belong to the car the grid handed in, and a swatch is a
   preference rather than a request for another plate number's figures. The pick
   is carried into the booking, which is the only place it is needed.

   The money opens the card, on one row above the photograph: the price on
   the left, the monthly on the right. What is asked up front stays below,
   under the two routes an order is taken on, because it is the only figure
   that changes between them.

   Under them the pair of actions: file a booking, or ask on WhatsApp — the
   two ways an order actually starts. */

/* Only the cars that are not simply standing there. "In stock" is the case a
   grid of stock is already making, so saying it on every card is a row of
   type that carries no information; reserved and sold do carry some, and get
   a chip over the photograph. */
const STATUS: Partial<Record<Vehicle['status'], string>> = {
  reserved: 'Reserved',
  sold: 'Sold',
}

type InventoryCardProps = {
  vehicle: Vehicle
  /** The shortlist itself rather than one flag: what is saved is the car on
      the card, whichever colour has been picked on it. */
  savedIds?: string[]
  onToggleSave?: (id: string) => void
}

function InventoryCard({ vehicle, savedIds, onToggleSave }: InventoryCardProps) {
  /* The cursor spotlight the rest of the site's cards carry (see card-glow):
     CSS draws the pool of light, this only feeds it coordinates. */
  const glowRef = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    const el = glowRef.current
    if (!el) return
    return pointerGlow(el)
  }, [])

  const [bookingOpen, setBookingOpen] = useState(false)

  /* The paints this one is offered in: its own, and every other colour the
     same trim and model year is standing on the floor in. The pick is local
     to the card and goes nowhere but the booking — see useColourPick. */
  const { colours, selected, selectedId, select } = useColourPick(vehicle)

  /* Which of the two routes the figure below is quoting. Card-local: a
     visitor comparing a grid is choosing per car, not setting a mode for
     the site. On spot first — it is the one most people are asking about
     when they are looking at a car that is standing on the floor, and on a
     car that cannot be had that way the picker falls to the one route it
     does offer. */
  const [route, setRoute] = useState<PurchaseRoute>('on-spot')

  const label = STATUS[vehicle.status]
  const monthly = vehicle.financingAvailable ? (vehicle.instalment ?? 0) : 0
  /* The routes this one is actually offered on: both on most of the floor,
     and a single one on a car the showroom will only book or only sell off
     the floor — which the picker captions rather than offering as a choice.
     Only a sold car drops the row entirely; there is nothing left to order. */
  const routes = purchaseOptionsFor(vehicle)
  const choice = vehicle.status !== 'sold'
  const bookingAvailable = vehicle.status === 'in-stock' && vehicle.availability !== 'on-spot'
  const saved = savedIds?.includes(vehicle.id) ?? false

  return (
    <article
      ref={glowRef}
      className="group card-glow relative flex h-full flex-col overflow-hidden rounded-2xl border border-line bg-raised shadow-card transition duration-500 ease-lux hover:-translate-y-1 hover:border-lime/35 hover:shadow-lift"
    >
      {/* ---------- the money ----------
          The two figures open the card, above the photograph: a grid of these
          is read by comparison, and the money is what the eye runs down the
          column for. Cash on the left, the monthly on the right, on one row —
          the two ways of paying for the same car, said side by side.

          Both are facts about the car and hold still; only the figure under
          the two route segments below changes when a route is picked, so the
          card does not reflow under the cursor. A swatch never moves them —
          the colour is what the buyer wants, not another car's price. */}
      <dl className="flex items-end justify-between gap-3 px-4 pt-3.5">
        <div className="min-w-0">
          <dt className="text-[11.5px] text-dim">Vehicle price</dt>
          <dd className="mt-1">
            {vehicle.price > 0 ? (
              <span className="flex items-baseline gap-1">
                <span className="text-[10.5px] font-medium text-dim">PKR</span>
                <strong className="font-display text-[16px] leading-none font-semibold tracking-[-0.015em] text-platinum tabular-nums">
                  {shortPrice(vehicle.price)}
                </strong>
              </span>
            ) : (
              <span className="text-[12px] font-medium text-lime-deep">On request</span>
            )}
          </dd>
        </div>

        {monthly > 0 ? (
          <div className="min-w-0 text-right">
            <dt className="text-[11.5px] text-dim">Instalment</dt>
            <dd className="mt-1 flex items-baseline justify-end gap-1">
              <span className="text-[10.5px] font-medium text-dim">PKR</span>
              <strong className="font-display text-[15px] leading-none font-semibold tracking-[-0.015em] text-lime tabular-nums">
                {pkr.format(monthly)}
              </strong>
              <span className="text-[11px] font-medium text-dim">/mo</span>
            </dd>
          </div>
        ) : null}
      </dl>

      {/* ---------- the picture ---------- */}
      {/* No plate of its own: the media is part of the card's surface, and
          the car stands on it. See VehicleMedia's Stage.

          It does not follow the swatch: a car is photographed once for its
          trim and those frames stand for every paint of it.

          What used to be captioned above it rides on it instead: the one
          thing the showroom wants said about this car, and — on a car that
          is not simply standing there — where it stands. Both are absent
          most of the time, which is the point: the picture carries the car,
          not a row of chips. */}
      <div className="relative mx-3 mt-3 aspect-16/10 overflow-hidden rounded-xl">
        <VehicleMedia vehicle={vehicle} source={vehicle.images[0] || ''} />

        {vehicle.tag || label ? (
          <div className="pointer-events-none absolute inset-x-2 top-2 flex flex-wrap items-center gap-1.5">
            {vehicle.tag ? (
              <span className="rounded-full bg-lime px-2.5 py-1 text-[10.5px] font-semibold tracking-[-0.01em] text-void shadow-action">
                {vehicle.tag}
              </span>
            ) : null}

            {label ? (
              <span className="rounded-full border border-white/15 bg-void/75 px-2.5 py-1 text-[10.5px] font-medium text-silver backdrop-blur-sm">
                {label}
              </span>
            ) : null}
          </div>
        ) : null}

        {/* Above the stretched title link — hence the raised z — so a press
            here saves the car rather than opening it. */}
        {onToggleSave ? (
          <button
            type="button"
            aria-pressed={saved}
            aria-label={`${saved ? 'Remove' : 'Save'} ${nameOf(vehicle)}`}
            onClick={() => onToggleSave(vehicle.id)}
            className={`absolute top-2 right-2 z-2 grid size-8 place-items-center rounded-full border border-white/12 bg-void/70 backdrop-blur-sm transition duration-200 ${
              saved
                ? 'text-lime'
                : 'text-silver hover:border-lime/40 hover:text-lime-deep'
            }`}
          >
            <Heart
              className="size-4"
              strokeWidth={1.8}
              fill={saved ? 'currentColor' : 'none'}
            />
          </button>
        ) : null}
      </div>

      {/* ---------- what it is, and what it costs ---------- */}
      <div className="flex flex-1 flex-col justify-end gap-1.5 px-4 pt-3.5 pb-4">
        {/* The title is the card's link, stretched over the whole surface, so
            the card is one click target and one accessible name. It opens
            this car, whichever colour has been picked on it. */}
        <h3 className="font-display text-[16px] leading-tight font-semibold tracking-[-0.015em] text-platinum">
          <Link
            to={`/cars/${vehicle.id}`}
            className="transition-colors duration-200 before:absolute before:inset-0 before:content-[''] hover:text-lime"
          >
            {vehicle.variant}
            <span className="sr-only"> — view details</span>
          </Link>
        </h3>

        {/* The routes an order is taken on — the pair to choose between, or
            the one this car is available on, said in a line. Above the
            stretched title link, like the buttons below, so a press picks a
            route rather than opening the car. */}
        {choice ? (
          <PurchaseRoutePicker
            options={routes}
            value={route}
            onChange={setRoute}
            name={nameOf(vehicle)}
            className="relative z-2 mt-2.5"
          />
        ) : null}

        {/* The paints, as swatches — one row, no legend: the row is short
            enough to read as a palette, and the name of the one picked is
            spelled out on the booking form where it matters.

            Pressing one records a preference and changes nothing else on the
            card. `relative z-2` for the same reason as the buttons below —
            the title link is stretched over the whole card, so anything
            meant to be pressed in its own right has to sit above it. */}
        <ColourSwatches
          colours={colours}
          value={selectedId}
          onChange={select}
          name={nameOf(vehicle)}
          className="relative z-2 mt-2"
        />

        {/* The two ways this business takes an order, on one row. They sit
            above the stretched title link — hence `relative` and the raised
            z — so a click on either is the action it says, not a navigation
            to the detail page.

            The booking is the deal, so it takes the width and the colour;
            WhatsApp is the aside, folded down to its own mark so it cannot
            compete with the order of the two. Sized a notch under the
            editorial card's pair, to match this card's smaller type. */}
        <div className={`relative z-2 mt-3 grid gap-2 ${bookingAvailable ? 'grid-cols-[1fr_auto]' : 'grid-cols-1'}`}>
          {bookingAvailable ? (
            <button
              type="button"
              onClick={() => setBookingOpen(true)}
              className="flex h-10 btn-sheen items-center justify-center gap-1.5 rounded-full bg-lime text-[12.5px] font-semibold tracking-[-0.01em] text-void shadow-action transition duration-300 hover:bg-lime-lift active:translate-y-px"
            >
              Book now
              <ArrowUpRight aria-hidden="true" strokeWidth={2} className="size-3.5 shrink-0" />
              <span className="sr-only">
                {' '}
                — {nameOf(vehicle)}
              </span>
            </button>
          ) : null}

          <a
            href={vehicleWhatsappHref(vehicle, selected?.name || vehicle.colour)}
            target="_blank"
            rel="noreferrer"
            aria-label={`WhatsApp — enquire about the ${nameOf(vehicle)}`}
            title="Enquire on WhatsApp"
            className={
              bookingAvailable
                ? 'grid size-10 place-items-center rounded-full border border-mint/40 bg-mint/8 text-mint-deep transition duration-200 hover:border-mint hover:bg-mint/15 active:translate-y-px'
                : 'flex h-10 items-center justify-center gap-2 rounded-full border border-mint/40 bg-mint/8 px-4 text-[12.5px] font-semibold text-mint-deep transition duration-200 hover:border-mint hover:bg-mint/15 active:translate-y-px'
            }
          >
            <WhatsAppIcon className="size-4.5 shrink-0" />
            {!bookingAvailable ? 'Enquire on WhatsApp' : null}
          </a>
        </div>
      </div>

      {/* Mounted only while open, so a grid of twenty cars is not also a grid
          of twenty hidden dialogs. The booking is filed against this car, in
          the colour the swatches were left on. */}
      {bookingOpen && bookingAvailable ? (
        <BookingModal
          vehicle={vehicle}
          colour={selected}
          open={bookingOpen}
          onClose={() => setBookingOpen(false)}
        />
      ) : null}
    </article>
  )
}

export default InventoryCard
